import { Lightbulb, Calendar, Package, ClipboardCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const tips = [
  {
    icon: Calendar,
    title: 'Frühzeitige Planung spart Stress',
    content: [
      '<strong>3 Monate vorher:</strong> Umzugstermin festlegen und Umzugsfirma kontaktieren. Wohnungsauflösung in der Schweiz und neue Wohnung in Deutschland organisieren.',
      '<strong>2 Monate vorher:</strong> Abmeldung bei Schweizer Behörden vorbereiten, Kündigungen (Strom, Internet, Versicherungen) einreichen.',
      '<strong>1 Monat vorher:</strong> Anmeldung bei deutschen Behörden klären, wichtige Dokumente zusammenstellen.',
      '<strong>2 Wochen vorher:</strong> Packen beginnen, nicht benötigte Gegenstände aussortieren oder spenden.',
    ],
  },
  {
    icon: Package,
    title: 'Richtig packen – aber wie?',
    content: [
      '<strong>Schwere Gegenstände in kleine Kartons:</strong> Bücher, Geschirr und schwere Gegenstände gehören in kleinere Kartons. So lassen sie sich besser tragen.',
      '<strong>Zerbrechliches gut schützen:</strong> Verwenden Sie Luftpolsterfolie, Packpapier und Decken. Teller hochkant stellen, nicht flach stapeln.',
      '<strong>Kartons beschriften:</strong> Schreiben Sie auf jeden Karton den Raum und grob den Inhalt. Das spart Zeit beim Auspacken.',
      '<strong>Essentials-Box:</strong> Packen Sie eine Kiste mit Dingen des täglichen Bedarfs (Kaffee, Zahnbürste, Handy-Ladegerät, etc.) – diese kommt als letztes ins und als erstes aus dem Transporter.',
    ],
  },
  {
    icon: ClipboardCheck,
    title: 'Zollformalitäten leicht gemacht',
    content: [
      '<strong>Inventarliste erstellen:</strong> Der Zoll benötigt eine detaillierte Liste Ihres Umzugsguts. Wir helfen Ihnen dabei oder erstellen diese für Sie.',
      '<strong>Wohnsitzbestätigung:</strong> Sie benötigen einen Nachweis über Ihren Umzug (Abmeldung Schweiz + Anmeldung Deutschland).',
      '<strong>Persönliche Dokumente:</strong> Pass oder Personalausweis bereithalten.',
      '<strong>Keine Sorge:</strong> Wir übernehmen die komplette Zollabwicklung für Sie – Sie müssen nicht am Zoll anwesend sein.',
    ],
  },
  {
    icon: Lightbulb,
    title: 'Tipps vom Profi',
    content: [
      '<strong>Fotos vor dem Abbau:</strong> Fotografieren Sie komplizierte Möbelkonstruktionen oder Kabelanschlüsse vor dem Abbau – das erleichtert den Aufbau.',
      '<strong>Kühlschrank & Gefriertruhe:</strong> 24 Stunden vor dem Umzug abtauen und trocknen lassen.',
      '<strong>Pflanzen:</strong> Grosse Pflanzen können problematisch sein. Prüfen Sie Einfuhrbestimmungen oder verschenken Sie sie.',
      '<strong>Wertsachen selbst transportieren:</strong> Schmuck, wichtige Dokumente und persönliche Wertgegenstände sollten Sie selbst mitnehmen.',
      '<strong>Adressänderungen:</strong> Denken Sie an Post-Nachsendeauftrag, Bank, Arbeitgeber, Versicherungen, Abos, etc.',
    ],
  },
];

export default function MovingTips() {
  return (
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-block bg-slate-800 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Umzugsratgeber
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Umzug Schweiz → Deutschland: Die ultimative Checkliste
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Ein internationaler Umzug will gut geplant sein. Mit unseren Profi-Tipps wird Ihr Umzug zum Kinderspiel.
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-8">
          {tips.map((tip, index) => {
            const Icon = tip.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 md:p-10 border-2 border-slate-100 hover:border-slate-300 transition"
              >
                <div className="flex items-start mb-6">
                  <div className="bg-slate-800 p-4 rounded-2xl mr-6 flex-shrink-0">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                      {tip.title}
                    </h3>
                  </div>
                </div>

                <ul className="space-y-4">
                  {tip.content.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      className="flex items-start text-slate-700 leading-relaxed"
                    >
                      <span className="bg-slate-800 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-0.5 flex-shrink-0">
                        {itemIndex + 1}
                      </span>
                      <span
                        className="[&_strong]:text-slate-900 [&_strong]:font-semibold"
                        dangerouslySetInnerHTML={{ __html: item }}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 md:p-12 border-2 border-slate-100 max-w-4xl mx-auto">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-4">
              Lieber alles aus einer Hand?
            </h3>
            <p className="text-slate-600 text-lg mb-8">
              Sie möchten sich nicht um die Details kümmern? Kein Problem! Wir übernehmen die komplette Planung, <Link to="/verpackungsmaterial" className="text-slate-900 font-semibold hover:underline">Verpackung</Link>, <Link to="/zollabwicklung" className="text-slate-900 font-semibold hover:underline">Zollabwicklung</Link> und Durchführung – Sie können sich entspannt zurücklehnen.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => {
                  const element = document.getElementById('kontakt');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-slate-800 hover:bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold transition shadow-lg hover:shadow-xl"
              >
                Rundum-Sorglos-Paket anfragen
              </button>
              <a
                href="tel:0800700100"
                className="bg-white hover:bg-slate-50 text-slate-800 px-8 py-4 rounded-lg font-semibold transition shadow-md hover:shadow-lg border-2 border-slate-200"
              >
                0800 700 100 (Gratis)
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
